/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// eslint-disable-next-line no-unused-vars
import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	remixReady: {
		id: 'malleable-ui-components.remix.remix-ready-toast.remixReady',
		defaultMessage: 'Your remix is ready',
		description: 'Text shown when the remix is ready',
	},
	updateReady: {
		id: 'malleable-ui-components.remix.remix-ready-toast.updateReady',
		defaultMessage: 'Your edits are ready',
		description: 'Text shown when the update is ready',
	},
	viewButton: {
		id: 'malleable-ui-components.remix.remix-ready-toast.viewButton',
		defaultMessage: 'View',
		description: 'Text for the button to view the remix',
	},
	closeButton: {
		id: 'malleable-ui-components.remix.remix-ready-toast.closeButton',
		defaultMessage: 'Close',
		description: 'Text for the button to close the remix',
	},
});

const styles = cssMap({
	container: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		display: 'flex',
		alignItems: 'center',
		minWidth: '360px',
		minHeight: '48px',
		gap: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overlay'),
		zIndex: 600,
	},
	absolute: {
		position: 'absolute',
	},
	readyMessage: {
		flexGrow: 1,
		flexShrink: 0,
		flexBasis: 'auto',
		paddingLeft: token('space.100'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
	},
});

interface RemixReadyToastProps {
	isUpdate?: boolean;
	showActions?: boolean;
	onView?: () => void;
	onClose?: (options?: { auto?: boolean }) => void;
}

const AUTO_DISMISS_DELAY_MS = 10000;
const SHORT_AUTO_DISMISS_DELAY_MS = 1000;

export function RemixReadyToast({
	isUpdate = false,
	onView,
	onClose,
	showActions = true,
}: RemixReadyToastProps = {}): JSX.Element {
	const { formatMessage } = useIntl();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [isAbsolute, setIsAbsolute] = useState(false);

	useLayoutEffect(() => {
		if (!wrapperRef.current) {
			return;
		}
		// no scrollParent, such as Jira description editor
		// if in the future, they do have scrollParent, then we need to check if offsetParent is in scrollParent
		const scrollParent = findOverflowScrollParent(wrapperRef.current);
		if (!scrollParent) {
			// wrapperRef.current.offsetParent is null when it's fixed
			// so we have to check the parentElement's offsetParent
			const offsetParent = wrapperRef.current.parentElement?.offsetParent;
			if (offsetParent) {
				const offsetParentRect = offsetParent.getBoundingClientRect();
				if (offsetParentRect.bottom < window.innerHeight) {
					setIsAbsolute(true);
				}
			}
		}
	}, []);

	useEffect(() => {
		const timeoutId = window.setTimeout(
			() => {
				onClose?.({ auto: true });
			},
			showActions ? AUTO_DISMISS_DELAY_MS : SHORT_AUTO_DISMISS_DELAY_MS,
		);

		return () => window.clearTimeout(timeoutId);
	}, [onClose, showActions]);

	return (
		<Box ref={wrapperRef} xcss={cx(styles.container, isAbsolute && styles.absolute)}>
			<CheckCircleIcon label="" color={token('color.icon.success')} />
			<Box xcss={styles.readyMessage}>
				{formatMessage(isUpdate ? messages.updateReady : messages.remixReady)}
			</Box>
			{showActions && (
				<>
					<Button appearance="primary" onClick={onView}>
						{formatMessage(messages.viewButton)}
					</Button>
					<IconButton
						spacing="compact"
						appearance="subtle"
						label={formatMessage(messages.closeButton)}
						icon={() => <CrossIcon label="" />}
						onClick={() => onClose?.()}
					/>
				</>
			)}
		</Box>
	);
}
